import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Play, Pause } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAudioPlayer } from "@/components/AudioPlayerProvider";

interface Purchase {
  id: string;
  license_type: string;
  price: number;
  created_at: string;
  beat_id: string;
  beats: {
    title: string;
    producer: string;
    image_url: string;
    mp3_url: string | null;
    wav_url: string | null;
    flp_url: string | null;
    is_deleted?: boolean;
  };
}

interface PurchaseHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PurchaseHistoryModal = ({ isOpen, onClose }: PurchaseHistoryModalProps) => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { playBeat, currentBeat, isPlaying, togglePlayPause } = useAudioPlayer();

  useEffect(() => {
    if (isOpen) {
      fetchPurchases();
    }
  }, [isOpen]);

  const fetchPurchases = async () => {
    setIsLoading(true);
    
    // Use service role to get all purchases, including those with deleted beats
    const { data, error } = await supabase.functions.invoke('get-user-purchases');

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load purchase history",
        variant: "destructive",
      });
    } else {
      // De-duplicate by beat_id + license_type, keeping the latest purchase
      const items: Purchase[] = (data || []) as Purchase[];
      const byKey = new Map<string, Purchase>();
      for (const p of items) {
        const key = p.price === 0 ? `${p.beat_id}|free` : `${p.beat_id}|${p.license_type}`;
        const existing = byKey.get(key);
        if (!existing) {
          byKey.set(key, p);
        } else {
          const prev = new Date(existing.created_at).getTime();
          const curr = new Date(p.created_at).getTime();
          if (curr > prev) byKey.set(key, p);
        }
      }
      const deduped = Array.from(byKey.values()).sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setPurchases(deduped);
    }
    setIsLoading(false);
  };

  const formatLicenseType = (type: string) => {
    switch (type) {
      case "mp3_lease":
        return "MP3 Lease";
      case "wav_lease":
        return "WAV Lease";
      case "flp":
        return "FLP File";
      case "semi_exclusive":
        return "Semi Exclusive";
      case "full_exclusive":
        return "Full Exclusive";
      default:
        return type;
    }
  };

  const getAvailableFormats = (purchase: Purchase) => {
    const { license_type, beats } = purchase;
    const formats = [];
    
    switch (license_type) {
      case "mp3_lease":
        if (beats.mp3_url) formats.push({ type: 'MP3', url: beats.mp3_url });
        break;
      case "wav_lease":
        if (beats.mp3_url) formats.push({ type: 'MP3', url: beats.mp3_url });
        if (beats.wav_url) formats.push({ type: 'WAV', url: beats.wav_url });
        break;
      case "flp":
        if (beats.mp3_url) formats.push({ type: 'MP3', url: beats.mp3_url });
        if (beats.wav_url) formats.push({ type: 'WAV', url: beats.wav_url });
        if (beats.flp_url) formats.push({ type: 'FLP', url: beats.flp_url });
        break;
      case "semi_exclusive":
      case "full_exclusive":
        if (beats.mp3_url) formats.push({ type: 'MP3', url: beats.mp3_url });
        if (beats.wav_url) formats.push({ type: 'WAV', url: beats.wav_url });
        if (beats.flp_url) formats.push({ type: 'FLP', url: beats.flp_url });
        break;
    }
    
    return formats;
  };

  const handleDownload = async (purchase: Purchase, formatType: string, downloadUrl: string) => {
    if (!downloadUrl) {
      toast({
        title: "Download Error",
        description: "Download file not available for this format.",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Download Starting",
        description: `Preparing ${formatType} download for ${purchase.beats.title}...`,
      });

      // Check if URL is a GCS or needs signed URL (FLP, or any gcs:// URL)
      const needsSignedUrl = formatType === 'FLP' || downloadUrl.startsWith('gcs://') || downloadUrl.startsWith('storage://');

      if (needsSignedUrl) {
        // Normalize to a storage reference: storage://bucket/path
        let fileRef = downloadUrl;
        try {
          if (downloadUrl.startsWith('http') && downloadUrl.includes('/storage/v1/object/')) {
            const u = new URL(downloadUrl);
            const parts = u.pathname.split('/').filter(Boolean);
            const idx = parts.findIndex(p => p === 'object');
            if (idx !== -1 && parts.length > idx + 2) {
              const bucket = parts[idx + 2];
              const path = parts.slice(idx + 3).join('/');
              fileRef = `storage://${bucket}/${path}`;
            }
          } else if (!downloadUrl.startsWith('storage://') && !downloadUrl.startsWith('gcs://')) {
            // Assume it's a raw path in the private bucket
            fileRef = `storage://beat-files/${downloadUrl}`;
          }
        } catch (e) {
          if (import.meta.env.DEV) console.warn('Failed to normalize fileRef, using raw', e);
        }

        // Build a suggested download filename preserving original extension
        let ext = formatType.toLowerCase();
        try {
          const rawPath = (downloadUrl || '').split('?')[0];
          const dot = rawPath.lastIndexOf('.');
          if (dot !== -1) {
            const detectedExt = rawPath.substring(dot + 1).toLowerCase();
            if (detectedExt) ext = detectedExt;
          }
        } catch {}
        const suggestedName = `${purchase.beats.title.replace(/[^a-zA-Z0-9]/g, '_')}_${formatType}.${ext}`;

        const { data, error } = await supabase.functions.invoke('generate-signed-download', {
          body: { fileRef, expiresIn: 3600, downloadAs: suggestedName },
        });

        if (error || !data?.signedUrl) {
          if (import.meta.env.DEV) console.error('Error generating signed download URL:', error);
          toast({
            title: 'Download Error',
            description: 'Could not prepare a secure download link. Please contact support.',
            variant: 'destructive',
          });
          return;
        }

        const link = document.createElement('a');
        link.href = data.signedUrl;
        link.download = suggestedName;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For public MP3/WAV files (standard Supabase URLs), use blob approach
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const fileExtension = formatType.toLowerCase();
        const fileName = `${purchase.beats.title.replace(/[^a-zA-Z0-9]/g, '_')}_${formatType}.${fileExtension}`;
        
        // Create a temporary URL for the blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
      }

      toast({
        title: "Download Complete",
        description: `${purchase.beats.title} (${formatType}) downloaded successfully!`,
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error("Download error:", error);
      toast({
        title: "Download Error",
        description: "Failed to download file. Please try again or contact support.",
        variant: "destructive",
      });
    }
  };

  const handlePlayBeat = (purchase: Purchase) => {
    const audioUrl = purchase.beats.mp3_url || purchase.beats.wav_url;
    if (!audioUrl) {
      toast({
        title: "Playback Error",
        description: "No audio file available for this beat.",
        variant: "destructive",
      });
      return;
    }

    const beat = {
      id: purchase.beat_id,
      title: purchase.beats.title,
      producer: purchase.beats.producer,
      mp3_url: purchase.beats.mp3_url,
      wav_url: purchase.beats.wav_url,
      image_url: purchase.beats.image_url,
    };

    if (currentBeat?.id === purchase.beat_id) {
      togglePlayPause();
    } else {
      playBeat(beat);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Purchase History</DialogTitle>
          <DialogDescription>
            View all your previous beat purchases and downloads.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No purchases yet
            </div>
          ) : (
            purchases.map((purchase) => (
              <Card key={purchase.id}>
                <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={purchase.beats.image_url || "/placeholder-beat.jpg"}
                        alt={purchase.beats.title}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h3 className={`font-semibold ${purchase.beats.title === "[Deleted Beat]" ? "text-muted-foreground" : ""}`}>
                          {purchase.beats.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          by {purchase.beats.producer}
                        </p>
                        {(purchase.beats.title === "[Deleted Beat]" || purchase.beats.is_deleted) && (
                          <p className="text-xs text-amber-600 mt-1">
                            This beat is no longer available, but you retain your license
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary">
                            {formatLicenseType(purchase.license_type)}
                          </Badge>
                          <span className="text-sm font-medium">
                            ${purchase.price}
                          </span>
                        </div>
                      </div>
                       <div className="flex flex-col items-end gap-2">
                         <div className="text-sm text-muted-foreground">
                           {new Date(purchase.created_at).toLocaleDateString()}
                         </div>
                         {(purchase.beats.title === "[Deleted Beat]" || purchase.beats.is_deleted) ? (
                           <div className="flex flex-col gap-1">
                             <Button
                               size="sm"
                               className="gap-2"
                               disabled={true}
                               variant="outline"
                             >
                               <Download size={16} />
                               Unavailable
                             </Button>
                           </div>
                         ) : (
                           <div className="flex flex-col gap-1">
                             {/* Play/Pause Button */}
                             {(purchase.beats.mp3_url || purchase.beats.wav_url) && (
                               <Button
                                 onClick={() => handlePlayBeat(purchase)}
                                 size="sm"
                                 className="gap-2 min-w-[100px]"
                                 variant={currentBeat?.id === purchase.beat_id && isPlaying ? "default" : "outline"}
                               >
                                 {currentBeat?.id === purchase.beat_id && isPlaying ? (
                                   <Pause size={14} />
                                 ) : (
                                   <Play size={14} />
                                 )}
                                 {currentBeat?.id === purchase.beat_id && isPlaying ? "Playing" : "Play"}
                               </Button>
                             )}
                             {/* Download Buttons */}
                             {getAvailableFormats(purchase).map((format) => (
                               <Button
                                 key={format.type}
                                 onClick={() => handleDownload(purchase, format.type, format.url)}
                                 size="sm"
                                 className="gap-2 min-w-[100px]"
                                 variant="outline"
                               >
                                 <Download size={14} />
                                 {format.type}
                               </Button>
                             ))}
                           </div>
                         )}
                       </div>
                    </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};